import { AppConstants } from '@/helpers/app.constants';
import { Utils } from '@/helpers/utils';
import { AppConfigService } from '@/services/app-config.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  ConnectorsService,
  IdentityProvider,
  IdentityProviderType,
  SubscriptionState,
  UserSession,
  UsersService
} from '@eemo/api-client';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AlertService } from './alert.service';
import { BridgeService } from './bridge/bridge.service';
import { DictionaryCacheService } from './dictionary-cache.service';

export class OidcState {
  returnUrl: string;
}

export class OidcResponse {
  // tslint:disable-next-line: variable-name
  access_token: string;
  // tslint:disable-next-line: variable-name
  token_type: string;
  // tslint:disable-next-line: variable-name
  expires_in: number;
  // tslint:disable-next-line: variable-name
  scope: string;
  // tslint:disable-next-line: variable-name
  id_token: string;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  public currentUserSubject: BehaviorSubject<UserSession>;
  public currentUser: Observable<UserSession>;
  private emailValidatedSubject: BehaviorSubject<boolean>;
  public emailValidated: Observable<boolean>;
  private subscriptionStateSubject: BehaviorSubject<SubscriptionState>;
  public subscriptionState: Observable<SubscriptionState>;
  private guestModeSubject: BehaviorSubject<boolean>;
  public guestMode: Observable<boolean>;
  private currentIdpSubject: BehaviorSubject<IdentityProvider>;
  public currentIdp: Observable<IdentityProvider>;
  private currentJwtSubject: BehaviorSubject<string>;
  public currentJwt: Observable<string>;
  private currentAvatarSubject: BehaviorSubject<Blob>;
  public currentAvatar: Observable<Blob>;

  private currentDomain: string;
  private userConnected = false;

  invalidLoginCount = 0;

  constructor(
    private usersService: UsersService,
    private cookieService: CookieService,
    public appConfigService: AppConfigService,
    private router: Router,
    private alertService: AlertService,
    private dictCacheService: DictionaryCacheService,
    private http: HttpClient,
    private connectorService: ConnectorsService
  ) {
    this.currentDomain = window.location.hostname;
    if (this.checkAuthCookie()) {
      this.currentUserSubject = new BehaviorSubject<UserSession>(
        JSON.parse(localStorage.getItem(AppConstants.STORAGE_KEY_CURRENT_USER))
      );
      this.emailValidatedSubject = new BehaviorSubject<boolean>(
        JSON.parse(localStorage.getItem(AppConstants.STORAGE_KEY_EMAIL_VALIDATED))
      );
      this.subscriptionStateSubject = new BehaviorSubject<SubscriptionState>(
        JSON.parse(localStorage.getItem(AppConstants.STORAGE_KEY_SUBSCRIPTION_STATE))
      );
      this.currentJwtSubject = new BehaviorSubject<string>(
        JSON.parse(localStorage.getItem(AppConstants.STORAGE_KEY_JWT))
      );
    } else {
      this.currentUserSubject = new BehaviorSubject<UserSession>(null);
      this.emailValidatedSubject = new BehaviorSubject<boolean>(null);
      this.subscriptionStateSubject = new BehaviorSubject<SubscriptionState>(null);
      this.currentJwtSubject = new BehaviorSubject<string>(null);
      this.currentIdpSubject = new BehaviorSubject<IdentityProvider>(null);
    }
    this.currentAvatarSubject = new BehaviorSubject<File>(null);

    this.currentIdpSubject = new BehaviorSubject<IdentityProvider>(
      JSON.parse(localStorage.getItem(AppConstants.STORAGE_KEY_IDP))
    );

    this.currentJwt = this.currentJwtSubject.asObservable();
    this.emailValidated = this.emailValidatedSubject.asObservable();
    this.subscriptionState = this.subscriptionStateSubject.asObservable();
    this.currentUser = this.currentUserSubject.asObservable();
    this.currentIdp = this.currentIdpSubject.asObservable();
    this.guestModeSubject = new BehaviorSubject<boolean>(appConfigService.isDemo());
    this.guestMode = this.guestModeSubject.asObservable();
    this.currentAvatar = this.currentAvatarSubject.asObservable();
  }

  getCurrentUserStorage() {
    if (this.checkAuthCookie()) {
      return;
    } else {
      return null;
    }
  }

  public get currentIdpValue(): IdentityProvider {
    return this.currentIdpSubject.value;
  }

  public get currentUserValue(): UserSession {
    return this.currentUserSubject.value;
  }

  public get currentJwtValue(): string {
    return this.currentJwtSubject.value;
  }

  public get currentAvatarValue(): Blob {
    return this.currentAvatarSubject.value;
  }

  generateNewJwtNonce(): string {
    const nonce = Utils.randomString(10);
    localStorage.setItem(AppConstants.STORAGE_KEY_JWT_NONCE, nonce);
    return nonce;
  }

  get jwtNonce(): string {
    return localStorage.getItem(AppConstants.STORAGE_KEY_JWT_NONCE);
  }

  redirectUserToLogin(userEmail: string, returnUrl?: string, recaptchaToken?: string): Observable<IdentityProvider> {
    returnUrl = returnUrl || '/';
    return this.usersService.getUserIdp(userEmail, null, recaptchaToken, this.appConfigService.applicationType).pipe(
      map((idp) => {
        this.updateCurrentIdp(idp);
        if (idp.type !== IdentityProviderType.B2C && idp.type !== IdentityProviderType.NONE) {
          // redirect to login page
          this.goToLoginPage(idp, userEmail, returnUrl);
        }
        // note : if B2C, no redirection is done as the token is provided by a oauth password grant request
        return idp;
      })
    );
  }

  updateCurrentIdp(idp: IdentityProvider) {
    this.currentIdpSubject.next(idp);
    localStorage.setItem(AppConstants.STORAGE_KEY_IDP, JSON.stringify(idp));
  }

  doB2CLogin(idp: IdentityProvider, userEmail: string, password: string, returnUrl: string): Observable<boolean> {
    const formData = new FormData();
    formData.append('username', userEmail);
    formData.append('password', password);

    const subject = new Subject<boolean>();
    this.http.post<OidcResponse>(idp.loginUrl, formData).subscribe({
      next: (data) => {
        this.invalidLoginCount = 0;
        this.connectUser(data.id_token, JSON.stringify({ returnUrl }), false).subscribe((result) =>
          subject.next(result)
        );
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 400) {
          if ((err.error?.error_description as string).includes('Account is disabled')) {
            this.alertService.accountSuspendedError();
          } else {
            if (this.invalidLoginCount >= 2) {
              this.alertService.connectionProblemError();
            } else {
              this.alertService.error('Identifiants invalides');
            }
            this.invalidLoginCount++;
          }
        }
        subject.next(false);
      }
    });
    return subject.asObservable();
  }

  private goToLoginPage(idp: IdentityProvider, userEmail: string, returnUrl: string) {
    // generate nonce value
    const nonce = this.generateNewJwtNonce();
    // set state value
    const state: OidcState = { returnUrl };
    window.location.href =
      idp.loginUrl +
      '&' +
      AppConstants.PARAM_NONCE +
      '=' +
      nonce +
      '&' +
      AppConstants.PARAM_STATE +
      '=' +
      encodeURIComponent(JSON.stringify(state)) +
      '&login_hint=' +
      userEmail +
      '&referer=' +
      window.location.origin;
  }

  connectUser(jwt: string, stateStr?: string, verifyNonce = true): Observable<boolean> {
    const subject = new Subject<boolean>();
    // parse JWT
    const jwtHelper = new JwtHelperService();
    const jwtDecoded = jwtHelper.decodeToken(jwt);
    const state: OidcState = stateStr ? JSON.parse(stateStr) : null;
    if (!jwtDecoded) {
      this.alertService.error("Erreur de validation de l'identité (token)", true);
      subject.next(false);
    }
    if (verifyNonce && jwtDecoded.nonce !== this.jwtNonce) {
      this.alertService.error("Erreur de validation de l'identité (nonce)", true);
      subject.next(false);
    }
    if (jwtHelper.isTokenExpired(jwt)) {
      this.alertService.error("Erreur de validation de l'identité (expiration)", true);
      subject.next(false);
    }
    localStorage.setItem(AppConstants.STORAGE_KEY_JWT, JSON.stringify(jwt));
    this.currentJwtSubject.next(jwt);

    const jwtExpirationDate = jwtHelper.getTokenExpirationDate(jwt);
    this.addCookieToken(jwt, jwtExpirationDate);

    this.usersService.getUserSession(this.appConfigService.applicationType).subscribe({
      next: (session) => {
        this.registerUserSession(session, state);
        BridgeService.askSubscribeToNotifications(true);
        // get the user's preferences
        this.usersService
          .getUserPreferences(session.userId)
          .subscribe((preferences) => (this.appConfigService.userPreferences = preferences));
        // get the user's available connectors
        this.connectorService
          .getConnectorUserAvailable(session.userId)
          .subscribe((connectors) => (this.appConfigService.availableConnectors = connectors));
        subject.next(true);
      },
      error: () => {
        subject.next(false);
      }
    });
    return subject.asObservable();
  }

  public assumeUser(): Observable<boolean> {
    const subject = new Subject<boolean>();
    this.usersService.getUserSession(this.appConfigService.applicationType).subscribe({
      next: (session) => {
        this.registerUserSession(session);
        // get the user's available connectors
        this.connectorService
          .getConnectorUserAvailable(session.userId)
          .subscribe((connectors) => (this.appConfigService.availableConnectors = connectors));
        subject.next(true);
      },
      error: () => {
        subject.next(false);
      }
    });
    return subject.asObservable();
  }

  public connectGuest(returnUrl: string) {
    this.usersService.getUserSession().subscribe(
      (session) => {
        this.registerUserSession(session);
        if (returnUrl) {
          this.router.navigate([returnUrl]);
        } else {
          this.router.navigate(['/home']);
        }
      },
      (error: HttpErrorResponse) => {
        if (error.status === 403) {
          this.alertService.error('Mode invité non actif', true);
          this.router.navigate(['/']);
        } else {
          this.alertService.apiServerError(error);
        }
      }
    );
  }

  private registerUserSession(user: UserSession, state?: OidcState) {
    localStorage.setItem(AppConstants.STORAGE_KEY_CURRENT_USER, JSON.stringify(user));

    this.updateSubscriptionState(user.subscriptionState);

    this.usersService.getUser(user.userId).subscribe((curUser) => {
      localStorage.setItem(AppConstants.STORAGE_KEY_EMAIL_VALIDATED, JSON.stringify(curUser.emailValidated));
      this.emailValidatedSubject.next(curUser.emailValidated);
    });

    // authentication succeed : check the dictionary cache version to refresh
    this.userConnected = true;
    this.currentUserSubject.next(user);
    this.appConfigService.showIntro = this.appConfigService.isDemo() || user.lastConnectionDate !== null;
    this.dictCacheService.checkDictionaryVersion(true);
    // redirect to the app
    if (state) {
      this.router.navigate([state.returnUrl]);
    }
  }

  validateUserEmail() {
    localStorage.setItem(AppConstants.STORAGE_KEY_EMAIL_VALIDATED, JSON.stringify(true));
    this.emailValidatedSubject.next(true);
  }

  updateSubscriptionState(state: SubscriptionState) {
    localStorage.setItem(AppConstants.STORAGE_KEY_SUBSCRIPTION_STATE, JSON.stringify(state));
    this.subscriptionStateSubject.next(state);
  }

  logout(alertMessage?: string) {
    // remove user from local storage and set current user to null
    //const idToken = this.currentJwtValue;
    // logout from IdP (Only B2C Idp)
    // FIXME Cors error when logout
    /*if (
      this.currentIdpValue &&
      this.currentIdpValue.type === IdentityProviderType.B2C &&
      this.currentIdpValue.logoutUrl
    )
      this.http.get(this.currentIdpValue.logoutUrl + '&id_token_hint=' + idToken).subscribe();
      */
    // remove local auth infos
    this.resetAuthCache();
    setTimeout(() => this.alertService.success(alertMessage ? alertMessage : 'Vous êtes déconnecté', true));
    this.router.navigate(['/login']);
  }

  public refreshUserSession() {
    this.usersService.getUserSession().subscribe((session) => {
      this.currentUserSubject.next(session);
      this.updateSubscriptionState(session.subscriptionState);
    });
  }

  private addCookieToken(token: string, expirationDate: Date) {
    this.deleteCookieToken();
    const cookieSecure = this.appConfigService.isHttps();
    this.cookieService.set(
      AppConstants.COOKIE_TOKEN,
      token,
      expirationDate,
      '/',
      this.currentDomain,
      cookieSecure,
      'Lax'
    );
  }

  saveUserLogin(username: string) {
    localStorage.setItem(AppConstants.STORAGE_KEY_USER_LOGIN, JSON.stringify(username));
  }

  getSavedUserLogin() {
    return JSON.parse(localStorage.getItem(AppConstants.STORAGE_KEY_USER_LOGIN));
  }

  getSavedUserIdp(): IdentityProvider {
    return JSON.parse(localStorage.getItem(AppConstants.STORAGE_KEY_IDP));
  }

  deleteSavedUserLogin() {
    localStorage.removeItem(AppConstants.STORAGE_KEY_USER_LOGIN);
  }

  deleteCookieToken() {
    this.cookieService.delete(AppConstants.COOKIE_TOKEN, '/', this.currentDomain);
  }

  checkAuthCookie(): boolean {
    return this.cookieService.check(AppConstants.COOKIE_TOKEN);
  }

  saveCurrentAvatar(file: Blob) {
    this.currentAvatarSubject.next(file);
    if (this.appConfigService.isMobileApp) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // remove the data:image/png;base64, header
        BridgeService.userAvatar((reader.result as string).split(',')[1]);
      };
      reader.readAsDataURL(file);
    }
  }

  resetAuthCache() {
    this.userConnected = false;
    localStorage.removeItem(AppConstants.STORAGE_KEY_CURRENT_USER);
    localStorage.removeItem(AppConstants.STORAGE_KEY_JWT);
    localStorage.removeItem(AppConstants.STORAGE_KEY_IDP);
    localStorage.removeItem(AppConstants.STORAGE_KEY_EMAIL_VALIDATED);
    this.deleteCookieToken();
    this.currentUserSubject.next(null);
    BridgeService.isAuthenticated(false);
    BridgeService.userInfo(null);
  }

  public isUserConnected(): boolean {
    return (
      this.currentUserValue !== null && (this.appConfigService.isDemo() || this.userConnected || this.checkAuthCookie())
    );
  }
}
